import React from 'react';
import { Link, NavLink } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import { TECH_SECTIONS } from '../../data/techSections';
import { slugify } from './TechUI';

const Sidebar = ({ current }) => (
  <aside className="hidden lg:block w-64 shrink-0" data-testid="tech-sidebar">
    <div className="sticky top-28">
      <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#FF7A00]/80" data-testid="tech-sidebar-title">Technology</span>
      <ol className="mt-4 space-y-1">
        {TECH_SECTIONS.map((s) => (
          <li key={s.slug}>
            <NavLink to={`/technology/${s.slug}`} data-testid={`tech-nav-${s.slug}`}
              className={({ isActive }) => `flex gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${isActive ? 'bg-[#FF7A00]/10 text-[#FF7A00] font-semibold' : 'text-white/55 hover:text-white hover:bg-white/[0.03]'}`}>
              {s.title}
            </NavLink>
            {s.slug === current && s.subs.length > 0 && (
              <ol className="ml-9 my-1 space-y-1 border-l border-white/10 pl-3">
                {s.subs.map((t, i) => (
                  <li key={t}><a href={`#${slugify(t)}`} className="block text-xs text-white/40 hover:text-white py-0.5">{t}</a></li>
                ))}
              </ol>
            )}
          </li>
        ))}
      </ol>
    </div>
  </aside>
);

const MobileTabs = ({ current }) => (
  <div className="lg:hidden -mx-4 px-4 mb-8 flex gap-2 overflow-x-auto pb-2" data-testid="tech-mobile-tabs">
    {TECH_SECTIONS.map((s) => (
      <Link key={s.slug} to={`/technology/${s.slug}`}
        className={`whitespace-nowrap rounded-full border px-3 py-1.5 text-xs ${s.slug === current ? 'border-[#FF7A00] text-[#FF7A00] bg-[#FF7A00]/10' : 'border-white/10 text-white/60'}`}>
        {s.title}
      </Link>
    ))}
  </div>
);

const PrevNext = ({ idx }) => {
  const prev = TECH_SECTIONS[idx - 1], next = TECH_SECTIONS[idx + 1];
  const card = 'group flex-1 rounded-2xl border border-white/10 bg-[#141414] p-5 hover:border-[#FF7A00]/40 transition-colors';
  return (
    <div className="flex flex-col sm:flex-row gap-3 mt-20 pt-10 border-t border-white/10">
      {prev ? (
        <Link to={`/technology/${prev.slug}`} className={card} data-testid="tech-prev">
          <span className="flex items-center gap-1 text-xs text-white/40"><ArrowLeft size={12} />Previous</span>
          <span className="block mt-1 font-semibold group-hover:text-[#FF7A00] transition-colors">{prev.title}</span>
        </Link>
      ) : <div className="flex-1" />}
      {next ? (
        <Link to={`/technology/${next.slug}`} className={`${card} text-right`} data-testid="tech-next">
          <span className="flex items-center justify-end gap-1 text-xs text-white/40">Next<ArrowRight size={12} /></span>
          <span className="block mt-1 font-semibold group-hover:text-[#FF7A00] transition-colors">{next.title}</span>
        </Link>
      ) : (
        <Link to="/contact" className={`${card} text-right`} data-testid="tech-next-contact">
          <span className="flex items-center justify-end gap-1 text-xs text-white/40">Next step<ArrowRight size={12} /></span>
          <span className="block mt-1 font-semibold group-hover:text-[#FF7A00] transition-colors">Request data</span>
        </Link>
      )}
    </div>
  );
};

export const TechLayout = ({ slug, lead, children }) => {
  const idx = TECH_SECTIONS.findIndex((s) => s.slug === slug);
  const s = TECH_SECTIONS[idx];
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Header />
      <div className="max-w-7xl mx-auto px-4 lg:px-8 pt-24 lg:pt-32 pb-16 flex gap-12">
        <Sidebar current={slug} />
        <main className="flex-1 min-w-0" data-testid={`tech-page-${slug}`}>
          <MobileTabs current={slug} />
          <span className="font-mono text-xs tracking-[0.2em] uppercase text-[#FF7A00]/80 block mb-3">Technology</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-6" data-testid="tech-page-title">{s.title}</h1>
          {lead}
          {children}
          <PrevNext idx={idx} />
        </main>
      </div>
      <Footer />
    </div>
  );
};
